import React, {useEffect, useState} from 'react';
import {FlatList, Platform, ScrollView, StyleSheet, View} from 'react-native';
import WebView from 'react-native-webview';
import {useFocusEffect} from '@react-navigation/native';
import {observer} from 'mobx-react-lite';

import FeedProductTileCard from '../../components/FeedProductTileCard';
import ActivityIndicator from '../../components/ui-controls/ActivityIndicator';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Markdown from '../../components/ui-controls/Markdown';
import Text from '../../components/ui-controls/Text';
import {useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import {
  AirtableAttachmentDetail,
  AirtableFeedItemEmbeddedJs,
} from '../../models/airtable';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import {OrderLine} from '../../transport/apiServiceResponse';
import {ArticleVideoType} from '../../utils/feedAndDiscover/constants';
import {getFeedDiscoverDetailGraphics} from '../../utils/feedAndDiscover/media';

const FeedDiscoverDetailScreen = observer(
  ({navigation, route}: RouteNavigatorScreenProps<AppRoute.ARTICLE>) => {
    const {feedStore, shopSelectionStore} = useStores();
    const {isWebDesktopScreen} = useWebScreenDimensions();

    const [headlineVideoPaused, setHeadlineVideoPaused] = useState(false);
    const [articleVideoPaused, setArticleVideoPaused] = useState(true);
    const [youtubeVideoPaused, setYoutubeVideoPaused] = useState(true);
    const [linkedProducts, setLinkedProducts] = useState<OrderLine[]>([]);
    const [isLoadingLinkedProducts, setIsLoadingLinkedProducts] =
      useState(false);

    const target = navigation.getId() as AppRoute;

    const article = route.params?.article;

    // Handle deep linking "404". TODO: Better way? In "linking.ts"?
    useEffect(() => {
      if (!route.params?.article) navigation.navigate(AppRoute.HOME);
    }, [article, feedStore.discoverList, navigation, route.params]);

    useEffect(() => {
      if (article?.productBarcodes) {
        (async function () {
          setLinkedProducts([]);
          setIsLoadingLinkedProducts(true);
          const linked = await feedStore.loadLinkedProducts(article);
          setLinkedProducts(linked as OrderLine[]);
          setIsLoadingLinkedProducts(false);
        })();
      }
    }, [article, feedStore, shopSelectionStore.selectedStore]);

    // This is to stop the video playing whenever we navigate away.
    useFocusEffect(
      React.useCallback(() => {
        // Do something when the screen is focused
        // component.current.startAutoplay(false);

        return () => {
          // Do something when the screen is unfocused
          // Useful for cleanup functions
          // component.current.stopAutoplay();
          setHeadlineVideoPaused(true);
          setArticleVideoPaused(true);
          setYoutubeVideoPaused(true);
        };
      }, []),
    );

    const renderEmbeddedJSVideo = ({
      src,
      web,
      mobile,
    }: AirtableFeedItemEmbeddedJs) => {
      const platformSpecificSrc = isWebDesktopScreen ? web?.src : mobile?.src;
      const html = platformSpecificSrc || src;

      let width = isWebDesktopScreen ? web?.width : mobile?.width;
      let height = isWebDesktopScreen ? web?.height : mobile?.height;
      width = width || '100%';
      height = height || '100%';

      const style = StyleSheet.create({
        main: {
          width,
          height,
        },
      });

      return (
        <WebView style={style.main} originWhitelist={['*']} source={{html}} />
      );
    };

    const getImage = (
      detail: AirtableAttachmentDetail | undefined,
      vidType: any,
      margin?: any,
    ) =>
      getFeedDiscoverDetailGraphics(
        detail,
        vidType,
        margin,
        article,
        // @ts-ignore // TODO: fix this
        setHeadlineVideoPaused,
        headlineVideoPaused,
        articleVideoPaused,
        youtubeVideoPaused,
      );

    // TODO
    if (!article) {
      return (
        <View>
          <Text>Article not found</Text>
        </View>
      );
    }

    return (
      <ContentView>
        <ScrollView
          style={{flex: 1}}
          showsVerticalScrollIndicator={false}
          showsHorizontalScrollIndicator={false}>
          {getImage(article?.heroImage, ArticleVideoType.HeadlineVideo)}
          <View style={styles.textCard}>
            <Text style={styles.headline}>{article?.headline}</Text>
            <Markdown>{article.firstCopy?.trimEnd()}</Markdown>
          </View>
          {getImage(article?.articleImage, ArticleVideoType.ArticleVideo, true)}
          {!!(target === AppRoute.HOME && article?.secondCopy) && (
            <View style={styles.textCard}>
              <Markdown>{article.secondCopy?.trimEnd()}</Markdown>
            </View>
          )}
          <View style={styles.separator} />
          {!!article?.youtube &&
            getImage({type: 'video'}, ArticleVideoType.YoutubeVideo)}
          {!!article?.embeddedJs && renderEmbeddedJSVideo(article.embeddedJs)}
          {!!(target === AppRoute.DISCOVER && article?.secondCopy) && (
            <View style={styles.textCard}>
              <Markdown>{article.secondCopy?.trimEnd()}</Markdown>
            </View>
          )}
          {article?.productBarcodes?.length > 0 &&
            (isLoadingLinkedProducts ? (
              <ContentView>
                <ActivityIndicator />
              </ContentView>
            ) : (
              <FlatList
                data={linkedProducts}
                style={styles.flatList}
                renderItem={({item}) => (
                  <FeedProductTileCard
                    key={`${item.id}-0`}
                    product={item}
                    source={target}
                  />
                )}
                horizontal
                showsHorizontalScrollIndicator={Platform.OS === 'web'}
              />
            ))}
        </ScrollView>
      </ContentView>
    );
  },
);

export default FeedDiscoverDetailScreen;

const styles = StyleSheet.create({
  separator: {
    marginBottom: 16,
  },
  textCard: {
    marginTop: 16,
  },
  headline: {
    fontSize: 20,
    color: theme.colour.text.heading.primary,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  copy: {
    fontSize: 16,
  },
  flatList: {
    marginTop: 24,
    marginBottom: 4,
  },
});
